/**
 * Articles filter plugin registry.
 *
 * Reuses the calls filter plugins 1:1 where they map cleanly
 * (Program, Project, Operator, Region, District, Barometer, Search) — same URL
 * serialization, same API param names, same saved-view keys — and adds two
 * article-specific plugins:
 *   - articleRoleFilter  ("Rola artykułu", main)
 *   - articlesSchemeFilter (headless scheme with the extra value 'pozostale')
 *
 * Placement differs from the calls registry per spec: Project and Operator are
 * MAIN filters on the articles page (advanced on calls). We override `placement`
 * and `order` via a thin spread wrapper so all plugin logic stays reused
 * (DRY — no fork of serialization code).
 *
 * @see docs/specifications/2026-06-08-articles-filters-unification/spec.md
 */

import { FilterPlugin } from "../../calls/filters/types";
import {
  createFilterRegistry,
} from "../../calls/filters/registry";

// Reused calls plugins
import { programFilter } from "../../calls/filters/plugins/ProgramFilter";
import { regionFilter } from "../../calls/filters/plugins/RegionFilter";
import { districtFilter } from "../../calls/filters/plugins/DistrictFilter";
import { searchFilter } from "../../calls/filters/plugins/SearchFilter";
import { OperatorFilterPlugin } from "../../calls/filters/plugins/OperatorFilter";
import { ProjectFilterPlugin } from "../../calls/filters/plugins/ProjectFilter";
import { BarometerFilterPlugin } from "../../calls/filters/plugins/BarometerFilter";

// Articles-specific plugins
import { articleRoleFilter } from "./ArticleRoleFilter";
import { articlesSchemeFilter } from "./ArticlesSchemeFilter";

/**
 * Re-place a reused plugin into a different bar position without forking its
 * serialization logic. Spreads the original plugin and overrides placement/order.
 */
function withPlacement<T>(
  plugin: FilterPlugin<T>,
  placement: FilterPlugin<T>["placement"],
  order: number
): FilterPlugin<T> {
  return { ...plugin, placement, order };
}

/**
 * Articles Region plugin: reuses the calls RegionFilter for UI / URL (`region`)
 * / saved-view (`regions`) serialization, but the GET /grants/articles API
 * expects the query param `regions` (plural) — unlike calls which uses `region`.
 * Override only toApiParams to match FindArticlesDto.regions.
 */
const articlesRegionFilter: FilterPlugin<string[]> = {
  ...regionFilter,
  placement: "main",
  order: 4,
  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((r) => params.append("regions", r));
  },
};

/**
 * Articles filter registry.
 *
 * Main (order):
 *   1 Program · 2 Projekt · 3 Operator · 4 Region · 5 Powiat · 6 Rola artykułu · 7 Search
 * Advanced:
 *   1 Barometr
 * Hidden:
 *   Scheme (kfs|bur|pozostale — UI via SavedViewsBar preset pills)
 */
export const articlesFilterRegistry = createFilterRegistry([
  // Main plugins
  withPlacement(programFilter, "main", 1) as FilterPlugin<unknown>,
  withPlacement(ProjectFilterPlugin, "main", 2) as FilterPlugin<unknown>,
  withPlacement(OperatorFilterPlugin, "main", 3) as FilterPlugin<unknown>,
  articlesRegionFilter as FilterPlugin<unknown>,
  withPlacement(districtFilter, "main", 5) as FilterPlugin<unknown>,
  articleRoleFilter as FilterPlugin<unknown>,
  withPlacement(searchFilter, "main", 7) as FilterPlugin<unknown>,
  // Advanced plugins
  withPlacement(BarometerFilterPlugin, "advanced", 1) as FilterPlugin<unknown>,
  // Hidden plugins (serialization only; UI via SavedViewsBar presets)
  articlesSchemeFilter as FilterPlugin<unknown>,
]);
